/**
 * ConnectorCard - Collapsible card showing a data connector with its linked systems.
 *
 * Displays connector name, type, date, and delete action in the header.
 * Expands to show active data connections with links to each system.
 */

import { useState } from "react"
import { Box, Card, Collapsible, HStack, IconButton, Text, VStack } from "@chakra-ui/react"
import { FaChevronDown, FaChevronRight, FaTrash } from "react-icons/fa6"
import { format } from "date-fns"
import Link from "@hydrologiq/shared/components/Link"

interface ConnectedSystem {
  systemId: string
  systemName: string
  externalId: string | null
  lastQueried: string | null
}

interface ConnectorCardProps {
  connector: {
    id: string
    type: string
    name: string
    createdAt: string | null
    systemCount: number
    connectedSystems?: ConnectedSystem[]
  }
  typeDisplayName: string
  onDelete?: (connectorId: string, systemCount: number) => void
  onNavigate?: () => void
}

export function ConnectorCard({ connector, typeDisplayName, onDelete, onNavigate }: ConnectorCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  const formattedDate = connector.createdAt
    ? format(new Date(connector.createdAt), "dd MMM yyyy")
    : "—"

  const hasConnections = connector.systemCount > 0
  const systems = connector.connectedSystems ?? []

  return (
    <Card.Root border="1px solid" borderColor="border" borderRadius="md" overflow="hidden">
      <Collapsible.Root open={isOpen} onOpenChange={(details) => setIsOpen(details.open)}>
        <Card.Body p={0}>
          <HStack justify="space-between" align="center" p={4}>
            <HStack gap={3} flex={1} cursor="pointer" onClick={() => setIsOpen(!isOpen)}>
              <Collapsible.Trigger asChild>
                <IconButton
                  aria-label={isOpen ? "Collapse" : "Expand"}
                  size="xs"
                  variant="ghost"
                  disabled={!hasConnections}
                >
                  {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                </IconButton>
              </Collapsible.Trigger>

              <VStack align="flex-start" gap={1}>
                <HStack gap={2}>
                  <Text fontWeight="semibold" fontSize="md">
                    {connector.name}
                  </Text>
                  {hasConnections && (
                    <Text fontSize="xs" color="gray.500">
                      ({connector.systemCount} {connector.systemCount === 1 ? "system" : "systems"})
                    </Text>
                  )}
                </HStack>
                <HStack gap={3}>
                  <Text fontSize="sm" color="fg.muted">
                    {typeDisplayName}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Created {formattedDate}
                  </Text>
                </HStack>
              </VStack>
            </HStack>

            <Box>
              {onDelete && (
                <IconButton
                  aria-label="Delete connector"
                  size="sm"
                  variant={"hydrologiq_icon" as any}
                  colorPalette="red"
                  onClick={() => onDelete(connector.id, connector.systemCount)}
                >
                  <FaTrash />
                </IconButton>
              )}
            </Box>
          </HStack>

          <Collapsible.Content>
            <Box borderTopWidth="1px" borderColor="border" px={4} py={3} bg="bg.subtle">
              {hasConnections ? (
                <VStack align="stretch" gap={2}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                    Connected Systems
                  </Text>
                  {systems.map((system) => (
                    <HStack key={system.systemId} justify="space-between" align="center" py={1}>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {system.systemName}
                        </Text>
                        <HStack gap={3}>
                          {system.externalId && (
                            <Text fontSize="xs" color="gray.500">
                              ID: {system.externalId}
                            </Text>
                          )}
                          <Text fontSize="xs" color="gray.500">
                            Last queried: {system.lastQueried
                              ? format(new Date(system.lastQueried), "dd MMM yyyy HH:mm")
                              : "Never"}
                          </Text>
                        </HStack>
                      </VStack>
                      <Link
                        href={`/systems/${system.systemId}/settings`}
                        onClick={() => onNavigate?.()}
                      >
                        Manage
                      </Link>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No connected systems
                </Text>
              )}
            </Box>
          </Collapsible.Content>
        </Card.Body>
      </Collapsible.Root>
    </Card.Root>
  )
}
